// Main application entry point
import '@fortawesome/fontawesome-free/css/all.min.css'
import '@/styles/main.css'
import { router } from '@/utils/router'
import { LandingPage } from '@/pages/landing'
import { PricingPage } from '@/pages/pricing'
import { DashboardPage } from '@/pages/dashboard'
import { StatisticsPage } from '@/pages/statistics'
import { FeaturesPage } from '@/pages/features'
import { LoginPage } from '@/pages/login'
import { RegisterPage } from '@/pages/register'
import { TermosDeUsoPage } from '@/pages/termos-de-uso'
import { PoliticaDePrivacidadePage } from '@/pages/politica-de-privacidade'
import { RGPDPage } from '@/pages/rgpd'
import { PaymentSuccessPage } from '@/pages/payment-success'
import { PaymentErrorPage } from '@/pages/payment-error'
import { AccountSettingsPage } from '@/pages/account-settings'
import { TripPurposesPage } from '@/pages/trip-purposes'
import { WhatsNewPage } from '@/pages/whats-new'
import { AdminPage } from '@/pages/admin'
import { CookieBanner, type CookiePreferences } from '@/components/cookie-banner'
import { CookieUtils } from '@/services/cookie-consent'

class MapasKMApp {
  private appElement: HTMLElement
  private cookieBanner!: CookieBanner // Definite assignment assertion - initialized in initializeCookieConsent

  constructor() {
    this.appElement = document.getElementById('app')!
    this.setupRoutes()
    this.initializeCookieConsent()
    this.init()
  }

  private setupRoutes(): void {
    // Register routes
    router.register('/', () => this.renderPage(new LandingPage()))
    router.register('/pricing', () => this.renderPage(new PricingPage()))
    router.register('/login', () => this.renderPage(new LoginPage()))
    router.register('/register', () => this.renderPage(new RegisterPage()))
    router.register('/dashboard', () => this.renderPage(new DashboardPage()), true) // requires auth
    router.register('/statistics', () => this.renderPage(new StatisticsPage()), true) // requires auth
    router.register('/settings', () => this.renderPage(new AccountSettingsPage()), true) // requires auth
    router.register('/trip-purposes', () => this.renderPage(new TripPurposesPage()), true) // requires auth
    router.register('/whats-new', () => this.renderPage(new WhatsNewPage()), true) // requires auth
    router.register('/admin', () => this.renderPage(new AdminPage()), true) // requires auth - admin role check done in page
    router.register('/funcionalidades', () => this.renderPage(new FeaturesPage()))

    // Legal pages
    router.register('/termos-de-uso', () => this.renderPage(new TermosDeUsoPage()))
    router.register('/politica-de-privacidade', () => this.renderPage(new PoliticaDePrivacidadePage()))
    router.register('/rgpd', () => this.renderPage(new RGPDPage()))

    // Payment pages
    router.register('/payment/success', () => this.renderPage(new PaymentSuccessPage()))
    router.register('/payment/error', () => this.renderPage(new PaymentErrorPage()))

    // Fallback route
    router.register('*', () => router.navigate('/'))
  }

  private async renderPage(page: any): Promise<void> {
    console.log('Rendering page:', page.constructor.name)

    // Clear app container
    this.clearApp()

    // Show loading state
    this.showLoading()

    try {
      // Render the page
      console.log('Calling page.render()...')
      const pageElement = await page.render()
      console.log('Page rendered successfully:', pageElement)

      // Clear loading state and show the page
      this.clearApp()
      this.appElement.appendChild(pageElement)
      console.log('Page added to DOM')
    } catch (error) {
      console.error('Failed to render page:', error)
      this.showError('Failed to load page')
    }
  }

  private clearApp(): void {
    while (this.appElement.firstChild) {
      this.appElement.removeChild(this.appElement.firstChild)
    }
  }

  private showLoading(): void {
    const loader = document.createElement('div')
    loader.style.display = 'flex'
    loader.style.alignItems = 'center'
    loader.style.justifyContent = 'center'
    loader.style.minHeight = '100vh'
    loader.innerHTML = `
      <div class="loading"></div>
      <span style="margin-left: 1rem; color: var(--text-secondary);">Loading...</span>
    `
    this.appElement.appendChild(loader)
  }

  private showError(message: string): void {
    this.clearApp()
    const error = document.createElement('div')
    error.style.display = 'flex'
    error.style.flexDirection = 'column'
    error.style.alignItems = 'center'
    error.style.justifyContent = 'center'
    error.style.textAlign = 'center'
    error.style.minHeight = '100vh'
    error.innerHTML = `
      <h2 style="color: #EF4444; margin-bottom: 1rem;">Error</h2>
      <p style="color: var(--text-secondary); margin-bottom: 2rem;">${message}</p>
      <button class="btn btn-primary" onclick="window.location.reload()">
        Retry
      </button>
    `
    this.appElement.appendChild(error)
  }

  private initializeCookieConsent(): void {
    // Initialize cookie banner with consent change handler
    this.cookieBanner = new CookieBanner((preferences: CookiePreferences) => {
      this.handleConsentChange(preferences)
    })

    // Listen for cookie settings trigger from footer
    document.addEventListener('show-cookie-settings', () => {
      if (this.cookieBanner) {
        this.cookieBanner.showSettings()
      }
    })
  }

  private async handleConsentChange(preferences: CookiePreferences): Promise<void> {
    console.log('Cookie preferences updated:', preferences)

    // Initialize or disable analytics based on consent
    if (preferences.analytics) {
      await CookieUtils.initializeAnalytics()
    }

    // Handle other cookie categories
    if (preferences.functional) {
      console.log('Functional cookies enabled')
      // Initialize functional features
    }

    if (preferences.marketing) {
      console.log('Marketing cookies enabled')
      // Initialize marketing/tracking scripts
    }

    if (preferences.third_party) {
      console.log('Third-party cookies enabled')
      // Initialize third-party services
    }
  }

  private async init(): Promise<void> {
    try {
      // Start the router
      router.start()
      console.log('Mapas KM app initialized successfully')
    } catch (error) {
      console.error('Failed to initialize app:', error)
      this.showError('Failed to initialize application')
    }
  }
}

// Initialize app when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  new MapasKMApp()
})

// Handle unhandled promise rejections
window.addEventListener('unhandledrejection', (event) => {
  console.error('Unhandled promise rejection:', event.reason)
  event.preventDefault()
})
